import { labelId } from '../../../../../../features/labels/labels';
import { ALARMAS_PS } from "../../../../lib/alarmCodes";
import { translateText } from "../../../../shared/utils/historyTranslation";
import {
  generatorIsOn,
  isTekFish,
  outputsAreInconsistent,
  resolveRunOrder,
} from "../../../../utils/generatorOutputs";

const ALARM_LABELS = {
  line_voltage_fail: "Fallo tension linea",
  phase_loss: "Perdida fase",
  phase_sequence_error: "Error secuencia fases",
  freq_out_of_range: "Frecuencia fuera rango",
  aux_voltage_fail: "Fallo tension auxiliar",
  asymmetric_current: "Corriente asimetrica",
  overcurrent_protection: "Sobrecorriente",
  blocked_rotor: "Rotor bloqueado",
  low_load: "Carga baja",
  long_startup: "Arranque largo",
  bypass_relay_fail: "Falla rele bypass",
  thermal_pre_alarm_motor: "Prealarma termica motor",
  thermal_pre_alarm_starter: "Prealarma termica arrancador",
  thermal_prot_motor: "Proteccion termica motor",
  thermal_prot_starter: "Proteccion termica arrancador",
  short_circuit_l1t1: "Corto L1-T1",
  short_circuit_l3t3: "Corto L3-T3",
  temp_sensor_fail: "Falla sensor temp.",
  low_line_voltage: "Tension linea baja",
  high_line_voltage: "Tension linea alta",
  low_motor_current: "Corriente motor baja",
  maintenance_request: "Mantencion requerida",
  fan_fail: "Falla ventilador",
  blocked_fan: "Ventilador bloqueado",
  system_error: "Error sistema",
  ua01_alarm: "Alarma usuario 1",
  ua02_alarm: "Alarma usuario 2",
  ua03_alarm: "Alarma usuario 3",
  ua04_alarm: "Alarma usuario 4",
};

export const SUMMARY_V3_MAP_TOOLTIP_EVENT = "summary-v3-map-tooltip-open";

function t(translate, key, fallback) {
  return translateText(translate, key, fallback);
}

export function getCenterTableViewModel(summary, translate) {
  const worstCage = summary.cages[0];
  const worstGenerator = summary.generators.items.find((generator) => generator.tone === "danger");
  const o2Value = fmtNum(summary.o2.avg);
  const o2Threshold = fmtNum(summary.o2Threshold);
  const o2HighThreshold = fmtNum(summary.o2HighThreshold);
  const o2Tone = getO2RangeTone(summary.o2.avg, summary.o2Threshold, summary.o2HighThreshold);
  const statusLabel = getSummaryStatusLabel(summary.status, translate);
  const belowThresholdCages = [];
  const injectingCages = [];
  const belowWithoutInjectionCages = [];
  const failureCages = [];
  const generatorProblemRows = summary.generators.items
    .filter((generator) => generator.tone === "danger")
    .map((generator) => [`${t(translate, "generator", "Generador")} ${generator.name}`, generator.statusLabel]);
  let reliableCount = 0;
  let o2DataCount = 0;

  // One pass keeps table hover/tooltips cheap even when centers contain many cages.
  summary.cages.forEach((cage) => {
    if (isNum(Number(cage.o2))) o2DataCount += 1;
    if (cage.reliability.reliable) reliableCount += 1;
    if (cage.belowThreshold) belowThresholdCages.push(cage);
    if (cage.injection.injecting) injectingCages.push(cage);
    if (isActionableBelowWithoutInjection(cage)) belowWithoutInjectionCages.push(cage);
    if (cage.visibleFailure) failureCages.push(cage);
  });

  return {
    o2Tone,
    o2Value,
    o2Rows: [
      [t(translate, "summary_v3_average", "Promedio"), o2Value],
      [t(translate, "summary_v3_min_threshold", "Umbral Min"), o2Threshold],
      [t(translate, "summary_v3_max_threshold", "Umbral Max"), o2HighThreshold],
      [t(translate, "summary_v3_range", "Rango"), getO2RangeLabel(o2Tone, summary.o2HighThreshold, translate)],
      [t(translate, "summary_v3_cages_with_data", "Jaulas con dato"), `${o2DataCount}/${summary.cagesTotal}`],
    ],
    cageRows: [
      [t(translate, "summary_v3_reliable_data", "Con dato confiable"), reliableCount],
      [t(translate, "summary_v3_unreliable_data", "Sin dato confiable"), summary.counts.unreliable],
      [t(translate, "summary_v3_with_problems", "Con Problemas"), summary.counts.failures],
    ],
    belowThresholdCages,
    injectingCages,
    belowWithoutInjectionCages,
    failureCages,
    problemCount: summary.counts.totalFailures,
    problemRows: [
      ...generatorProblemRows,
      ...failureCages.map((cage) => [`${t(translate, "jaula", "Jaula")} ${cage.alias}`, getCageFailureDetail(cage, translate)]),
    ].slice(0, 6),
    problemExtraRows: [
      ...generatorProblemRows,
      ...failureCages.map((cage) => [`${t(translate, "jaula", "Jaula")} ${cage.alias}`, getCageFailureDetail(cage, translate)]),
    ].slice(6),
    generatorsLabel: getGeneratorsSummaryLabel(summary.generators, translate),
    generatorRows: getGeneratorsTooltipRows(summary.generators, translate),
    worstCageLabel: worstGenerator
      ? `${t(translate, "generator", "Generador")} ${worstGenerator.name} - ${worstGenerator.statusLabel}`
      : worstCage
      ? `${t(translate, "jaula", "Jaula")} ${worstCage.alias} - ${getCageShortTag(worstCage, translate)}`
      : "-",
    worstCageRows: worstGenerator
      ? [[t(translate, "generator", "Generador"), worstGenerator.name], [t(translate, "status", "Estado"), worstGenerator.statusLabel]]
      : worstCage
      ? getCageDetailRows(worstCage, translate)
      : [[t(translate, "status", "Estado"), t(translate, "summary_v3_no_cages", "Sin jaulas")]],
    lastUpdateLabel: formatCompactDateTime(summary.lastUpdate),
    lastUpdateRows: [
      [t(translate, "ultima_medicion", "Ultima medicion"), formatCompactDateTime(summary.lastUpdate)],
      [t(translate, "summary_v3_reliability", "Confiabilidad"), summary.reliability.label],
    ],
    statusLabel,
    statusRows: [
      [t(translate, "summary_v3_criticality", "Criticidad"), statusLabel],
      [t(translate, "summary_v3_critical_generators", "Generadores criticos"), summary.generators.problem],
      [t(translate, "summary_v3_reliability", "Confiabilidad"), summary.reliability.label],
      [t(translate, "summary_v3_last_update", "Ultima actualizacion"), formatCompactDateTime(summary.lastUpdate)],
    ],
  };
}

export function getAlarmTrendPoints(cage, translate) {
  const lm = cage?.bomba?.latest_measurements || {};
  const ts = toArray(lm?.TS);
  const alarms = toArray(lm?.alarms);
  const pointCount = Math.max(ts.length, alarms.length);

  return Array.from({ length: pointCount }, (_, index) => {
    const codes = normalizeAlarmCodes(alarms[index]);
    const timeValue = ts[index] || cage?.lastUpdate;

    return {
      key: `${timeValue ?? "sin-fecha"}-${index}`,
      label: formatCompactDateTime(timeValue),
      shortLabel: formatAlarmShortTime(timeValue),
      count: codes.length,
      detail: codes.length
        ? codes.map((code) => `${code}: ${getAlarmLabel(code, translate)}`).join(" / ")
        : t(translate, "summary_v3_no_alarms", "Sin alarmas"),
    };
  });
}

function normalizeAlarmCodes(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((code) => String(code ?? "").trim())
    .filter(Boolean);
}

function formatAlarmShortTime(value) {
  const parsed = parseDashboardDateTime(value);
  if (!parsed) return "--";
  try {
    return new Intl.DateTimeFormat("es-CL", {
      timeZone: "America/Santiago",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(parsed);
  } catch {
    return "--";
  }
}

export function getSummaryStatusLabel(status, translate) {
  const labels = {
    critical: t(translate, "summary_v3_status_critical", "CRITICO"),
    warning: t(translate, "summary_v3_status_warning", "ALERTA"),
    injecting: t(translate, "summary_v3_status_injecting", "INYECTANDO"),
    normal: t(translate, "summary_v3_status_normal", "NORMAL"),
    offline: t(translate, "summary_v3_status_no_data", "SIN DATOS"),
    stale: t(translate, "summary_v3_status_stale", "DATO VIEJO"),
  };
  return labels[status] || labels.normal;
}

function getCenterFullId(center) {
  return center?.centro || center?.modulos?.[0]?.cans?.[0]?.centro_id || "";
}

function getCenterDisplayName(center) {
  const fullId = getCenterFullId(center);
  if (!fullId) return "-";
  const parts = String(fullId).split("-");
  return parts[parts.length - 1]?.trim() || "-";
}

function getCenterOrderKey(center) {
  const fullId = getCenterFullId(center);
  if (fullId) return normalizeDashboardKey(fullId);
  return normalizeDashboardKey(`${center?.region ?? ""}-${center?.area ?? ""}-${center?.centro ?? ""}`);
}

function getPumpSelectionKey(bomba, context = {}) {
  const parts = [
    context.centerId ?? bomba?.centro_id,
    context.moduleId ?? bomba?.modulo,
    context.canId ?? bomba?.can_id ?? bomba?.can,
    bomba?.bomba_id ?? bomba?.alias ?? context.index,
  ];

  const key = parts
    .map((part) => normalizeDashboardKey(part))
    .filter(Boolean)
    .join("|");

  return key || `pump-${context.index ?? "unknown"}`;
}

function normalizeDashboardKey(value) {
  return String(value ?? "").trim().toLowerCase();
}

function getCenterThresholds(center) {
  const raw = Array.isArray(center?.umbrales) ? center.umbrales[0] : center?.umbrales;
  return {
    low: firstAvailable(raw?.setpoin_on, raw?.SetPoint, raw?.Min, raw?.min),
    high: firstAvailable(raw?.setpoin_off, raw?.SetPointOFF, raw?.Max, raw?.max),
  };
}

function getCageThresholds(bomba, center) {
  const centerThresholds = getCenterThresholds(center);
  const low = firstAvailable(bomba?.SetPoint, bomba?.setpoint_on, centerThresholds.low);
  const high = firstAvailable(bomba?.SetPointOFF, bomba?.setpoint_off, centerThresholds.high);
  return { low, high };
}

function getO2Delta(bomba, center) {
  const { low, high } = getCageThresholds(bomba, center);
  const lm = bomba?.latest_measurements || {};
  const o2 = firstAvailable(lastOf(toArray(lm?.O2)), bomba?.O2, bomba?.O2_bckp);
  const threshold = low;
  return {
    o2,
    threshold,
    highThreshold: high,
    delta: isNum(Number(o2)) && isNum(Number(threshold)) ? Number(o2) - Number(threshold) : null,
  };
}

function getO2RangeTone(o2, setPoint, setPointOff) {
  if (!isNum(Number(o2))) return "unknown";
  if (isNum(Number(setPoint)) && Number(o2) < Number(setPoint)) return "low";
  if (isNum(Number(setPointOff)) && Number(o2) <= Number(setPointOff)) return "between";
  return "ok";
}

function getO2RangeLabel(tone, setPointOff = null, translate) {
  const labels = {
    low: t(translate, "summary_v3_below_min_threshold_lower", "Bajo umbral min"),
    between: t(translate, "summary_v3_between_thresholds", "Entre umbrales"),
    ok: isNum(Number(setPointOff))
      ? t(translate, "summary_v3_above_max_threshold_lower", "Sobre umbral max")
      : t(translate, "summary_v3_above_min_threshold_lower", "Sobre umbral min"),
    unknown: t(translate, "vpsa3d_no_data", "Sin dato"),
  };
  return labels[tone] || labels.unknown;
}

export function canViewSensorFailures(cargo) {
  const currentCargo = cargo ?? (typeof window !== "undefined" ? window.localStorage.getItem("cargo") : "");
  const normalized = normalizeDashboardKey(currentCargo).replace(/[.\s_-]+/g, "");
  return normalized === "superusuario" || normalized === "ti";
}

function firstPresent(...vals) {
  for (const value of vals) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function getTemperatureValue(bomba) {
  const lm = bomba?.latest_measurements || {};
  return firstPresent(lastOf(toArray(lm?.Temp)), bomba?.Temp, bomba?.TEMP, bomba?.TEMP_bckp);
}

function getSalinityValue(can) {
  return firstPresent(can?.sal, can?.salinidad, can?.salinity, can?.salinity_sensor?.Salinity);
}

function isMissingOrNegative(value) {
  if (value === null || value === undefined || value === "") return true;
  const numeric = Number(value);
  return !Number.isFinite(numeric) || numeric < 0;
}

function getSensorProblems(bomba, can, translate) {
  const temperature = getTemperatureValue(bomba);
  const salinity = getSalinityValue(can);
  const problems = [];

  if (bomba?.O2 <= 0 || bomba?.O2 === null || !bomba?.O2_Modbus_Status) {
    problems.push({
      type: "o2",
      label: t(translate, "summary_v3_sensor_o2", "Sensor O2"),
      detail: `O2 ${fmtNum(bomba?.O2)} / Modbus ${bomba?.O2_Modbus_Status ? "OK" : t(translate, "falla", "Falla")}`,
    });
  }

  if (isMissingOrNegative(temperature)) {
    problems.push({
      type: "temperature",
      label: t(translate, "summary_v3_temperature_sensor", "Sensor temperatura"),
      detail: `Temp ${fmtNum(temperature)}`,
    });
  }

  if (isMissingOrNegative(salinity)) {
    problems.push({
      type: "salinity",
      label: t(translate, "summary_v3_salinity_sensor", "Sensor salinidad"),
      detail: `${t(translate, "salinidad", "Salinidad")} ${fmtNum(salinity)}`,
    });
  }

  return problems;
}

function getInjectionState(bomba, status = {}, translate) {
  const lm = bomba?.latest_measurements || {};
  const injecting = Boolean(Number(firstAvailable(lastOf(toArray(lm?.Inyecting_O2)), bomba?.Inyecting_O2)));
  const belowThreshold = Boolean(status.belowThreshold);
  const modeAuto = Boolean(bomba?.InSel);
  const turnedOff = Object.prototype.hasOwnProperty.call(bomba || {}, "InStart") && !Boolean(Number(bomba?.InStart));
  const aboveHighThreshold =
    isNum(Number(status.o2)) &&
    isNum(Number(status.highThreshold)) &&
    Number(status.o2) > Number(status.highThreshold);

  let state = "idle";
  let label = t(translate, "summary_v3_no_injection", "Sin inyeccion");
  let inconsistent = false;
  let shouldInject = false;

  if (belowThreshold && !injecting) {
    state = turnedOff ? "off" : modeAuto ? "auto-missing" : "manual-off";
    label = turnedOff
      ? t(translate, "summary_v3_turned_off_f", "Apagada")
      : modeAuto
      ? t(translate, "summary_v3_auto_no_injection", "Auto sin inyeccion")
      : t(translate, "manual", "Manual");
    inconsistent = modeAuto && !turnedOff;
    shouldInject = modeAuto && !turnedOff;
  } else if (injecting && aboveHighThreshold) {
    state = "over";
    label = t(translate, "summary_v3_injects_above_threshold", "Inyecta sobre umbral");
    inconsistent = true;
  } else if (injecting) {
    state = "injecting";
    label = t(translate, "summary_v3_legend_injecting", "Inyectando");
  }

  return { state, label, injecting, inconsistent, shouldInject, modeAuto, turnedOff };
}

function getDataReliability(bomba, center, translate) {
  const connected = Boolean(bomba?.Device_Modbus_Status);
  const sensorOk = bomba?.O2 !== null && bomba?.O2_Modbus_Status !== false;
  const hasNoO2Data = Number(bomba?.O2_bckp) === -1;
  const latestTs = getLatestTimestampFromCage(bomba) || center?.hora;
  const stale = isMeasurementStale(latestTs, 20);

  if (!connected) return { status: "offline", label: t(translate, "sin_com", "Sin comunicacion"), tone: "offline", reliable: false };
  if (!sensorOk || hasNoO2Data) return { status: "sensor", label: t(translate, "summary_v3_sensor_o2", "Sensor O2"), tone: "danger", reliable: false };
  if (stale) return { status: "stale", label: t(translate, "summary_v3_stale_data", "Dato viejo"), tone: "stale", reliable: false };
  return { status: "ok", label: "OK", tone: "normal", reliable: true };
}

function getKnownAlarmCodes(bomba) {
  if (!Array.isArray(bomba?.alarms)) return [];
  return bomba.alarms
    .map((code) => String(code ?? "").trim())
    .filter((code) => Boolean(code) && Boolean(ALARMAS_PS[code]));
}

function getAlarmLabel(code, translate) {
  const alarmKey = ALARMAS_PS[code];
  if (!alarmKey) return t(translate, "alerta", "Alarma");
  return t(translate, alarmKey, ALARM_LABELS[alarmKey] || String(alarmKey).replaceAll("_", " "));
}

function getCageCurrentStatus(bomba, context = {}) {
  const { center, can } = context;
  const { translate } = context;
  const selectionKey = getPumpSelectionKey(bomba, {
    centerId: context.centerId,
    moduleId: context.moduleId,
    canId: context.canId,
    index: context.index,
  });
  const o2Data = getO2Delta(bomba, center);
  const belowThreshold =
    isNum(Number(o2Data.o2)) &&
    isNum(Number(o2Data.threshold)) &&
    Number(o2Data.o2) < Number(o2Data.threshold);
  const o2RangeTone = getO2RangeTone(o2Data.o2, o2Data.threshold, o2Data.highThreshold);
  const reliability = getDataReliability(bomba, center, translate);
  const temperature = getTemperatureValue(bomba);
  const salinity = getSalinityValue(can);
  const sensorProblems = getSensorProblems(bomba, can, translate);
  const injection = getInjectionState(bomba, { ...o2Data, belowThreshold }, translate);
  const sensorFailure = reliability.status === "sensor";
  const commFailure = reliability.status === "offline";
  const emergencyStop = Boolean(Number(bomba?.InStop));
  const alarmCodes = getKnownAlarmCodes(bomba);
  const alarmFailure = alarmCodes.length > 0;
  const linePressure = firstAvailable(can?.presion, bomba?.presion, bomba?.pressure);
  const lowPressure = Boolean(bomba?.aviot_status) || (isNum(Number(linePressure)) && Number(linePressure) < 3.5);
  const pressureInjectionFailure =
    belowThreshold &&
    injection.modeAuto &&
    !injection.turnedOff &&
    !injection.injecting &&
    lowPressure;
  const operationalFailure = pressureInjectionFailure || alarmFailure;

  return {
    bomba,
    center,
    centerKey: context.centerKey,
    centerName: context.centerName,
    selectionKey,
    alias: bomba?.alias ?? bomba?.bomba_id ?? context.index + 1,
    moduleId: context.moduleId ?? bomba?.modulo,
    canId: context.canId ?? bomba?.can_id ?? bomba?.can,
    o2: o2Data.o2,
    temperature,
    salinity,
    threshold: o2Data.threshold,
    highThreshold: o2Data.highThreshold,
    delta: o2Data.delta,
    belowThreshold,
    o2RangeTone,
    reliability,
    injection,
    sensorFailure,
    sensorProblems,
    sensorProblemLabel: sensorProblems.map((problem) => problem.label).join(", "),
    commFailure,
    emergencyStop,
    alarmFailure,
    alarmCodes,
    linePressure,
    lowPressure,
    pressureInjectionFailure,
    operationalFailure,
    hasFailure: operationalFailure,
    lastUpdate: getLatestTimestampFromCage(bomba) || center?.hora,
  };
}

function getCageSeverity(cageStatus) {
  if (!cageStatus.reliability.reliable && !cageStatus.belowThreshold) return "offline";
  if (cageStatus.alarmFailure) return "critical";
  if (cageStatus.belowThreshold && !cageStatus.injection.injecting && cageStatus.injection.turnedOff) return "normal";
  if (cageStatus.belowThreshold && !cageStatus.injection.injecting && cageStatus.injection.modeAuto) return "critical";
  if (cageStatus.injection.inconsistent || cageStatus.hasFailure) return "warning";
  if (cageStatus.belowThreshold && !cageStatus.injection.injecting) return "warning";
  return "normal";
}

function getCageSeverityScore(cage) {
  let score = 0;
  const belowByInactiveOff = cage.belowThreshold && !cage.injection.injecting && cage.injection.turnedOff;
  if (cage.belowThreshold && !cage.injection.injecting && cage.injection.modeAuto && !belowByInactiveOff) score += 100;
  if (cage.belowThreshold && !belowByInactiveOff) score += 45;
  if (cage.pressureInjectionFailure) score += 35;
  if (cage.delta < 0 && !belowByInactiveOff) score += Math.min(45, Math.abs(cage.delta) * 18);
  if (cage.injection.inconsistent) score += 38;
  if (cage.alarmFailure) score += 60;
  if (cage.reliability.status === "stale") score += 18;
  return Math.round(score);
}

function isActionableBelowWithoutInjection(cage) {
  return Boolean(cage?.belowThreshold && !cage?.injection?.injecting && !cage?.injection?.turnedOff);
}

function flattenSummaryCages(center, translate) {
  const centerKey = getCenterOrderKey(center);
  const centerName = labelId(getCenterFullId(center), "rac", getCenterDisplayName(center));
  const cages = [];

  toArray(center?.modulos).forEach((modulo, moduleIndex) => {
    const moduleId = modulo?.cans?.[0]?.modulo ?? modulo?.id ?? moduleIndex + 1;
    toArray(modulo?.cans).forEach((can, canIndex) => {
      const canId = can?.can_id ?? can?.can ?? canIndex + 1;
      toArray(can?.bombas).forEach((bomba, index) => {
        const current = getCageCurrentStatus(bomba, {
          center,
          can,
          centerKey,
          centerName,
          translate,
          centerId: getCenterFullId(center),
          moduleId,
          canId,
          index,
        });
        const severity = getCageSeverity(current);
        cages.push({
          ...current,
          severity,
          severityScore: getCageSeverityScore({ ...current, severity }),
        });
      });
    });
  });

  return cages.sort((a, b) => b.severityScore - a.severityScore);
}

export function getCenterSummary(center, prefilteredCages = null, options = {}) {
  const includeSensorFailures = Boolean(options.includeSensorFailures);
  const translate = options.translate;
  const baseCages = prefilteredCages || flattenSummaryCages(center, translate);
  const cages = baseCages
    .map((cage) => {
      const visibleSensorProblems = includeSensorFailures ? cage.sensorProblems : [];
      const visibleFailure = cage.commFailure || cage.operationalFailure || visibleSensorProblems.length > 0;
      return {
        ...cage,
        visibleFailure,
        visibleSensorProblems,
        visibleSensorProblemLabel: visibleSensorProblems.map((problem) => problem.label).join(", "),
        severityScore: cage.severityScore + (includeSensorFailures ? cage.sensorProblems.length * 82 : 0),
        severity: includeSensorFailures && cage.sensorProblems.length ? "critical" : cage.severity,
      };
    })
    .sort((a, b) => b.severityScore - a.severityScore);
  const o2Values = cages.map((cage) => cage.o2).filter((value) => isNum(Number(value))).map(Number);
  const operationalFailures = cages.filter((cage) => cage.operationalFailure).length;
  const sensorFailures = cages.reduce((sum, cage) => sum + cage.visibleSensorProblems.length, 0);
  const failures = cages.filter((cage) => cage.visibleFailure).length;
  const reliableCages = cages.filter((cage) => cage.reliability.reliable).length;
  const latestTimes = cages
    .map((cage) => parseDashboardDateTime(cage.lastUpdate)?.getTime())
    .filter(Number.isFinite);
  const latestTime = latestTimes.length ? new Date(Math.max(...latestTimes)).toISOString() : center?.hora;
  const generators = getCenterGeneratorsSummary(center, translate);

  const counts = {
    belowThreshold: cages.filter((cage) => cage.belowThreshold).length,
    belowWithoutInjection: cages.filter(isActionableBelowWithoutInjection).length,
    injecting: cages.filter((cage) => cage.injection.injecting).length,
    manualCages: cages.filter((cage) => !cage.injection.modeAuto).length,
    injectionInconsistent: cages.filter((cage) => cage.injection.inconsistent).length,
    failures,
    generatorFailures: generators.problem,
    totalFailures: failures + generators.problem,
    operationalFailures,
    sensorFailures,
    unreliable: cages.length - reliableCages,
  };

  let status = "normal";
  if (counts.injecting) status = "injecting";
  if (!cages.length || reliableCages === 0) status = "offline";
  if (counts.belowWithoutInjection || counts.injectionInconsistent || counts.failures || counts.unreliable || generators.warning) status = "warning";
  if (cages.some((cage) => cage.severity === "critical") || generators.problem) status = "critical";

  const reliabilityRatio = cages.length ? reliableCages / cages.length : 0;
  const reliability =
    reliabilityRatio >= 0.85
      ? { label: t(translate, "summary_v3_reliability_high", "Alta"), tone: "normal" }
      : reliabilityRatio >= 0.55
      ? { label: t(translate, "summary_v3_reliability_medium", "Media"), tone: "warning" }
      : { label: t(translate, "summary_v3_reliability_low", "Baja"), tone: "danger" };

  const summary = {
    center,
    centerKey: getCenterOrderKey(center),
    centerName: labelId(getCenterFullId(center), "rac", getCenterDisplayName(center)),
    status,
    cages,
    cagesTotal: cages.length,
    counts,
    reliability,
    generators,
    lastUpdate: latestTime,
    o2: {
      avg: o2Values.length ? o2Values.reduce((sum, value) => sum + value, 0) / o2Values.length : null,
    },
    o2Threshold: firstAvailable(...cages.map((cage) => cage.threshold)),
    o2HighThreshold: firstAvailable(...cages.map((cage) => cage.highThreshold)),
  };

  return {
    ...summary,
    severityScore: getCenterCriticalityScore(summary),
  };
}

export function getCenterCriticalityScore(summary) {
  if (!summary) return 0;
  const worstCageScore = Math.max(0, ...summary.cages.map((cage) => cage.severityScore || 0));
  const score =
    worstCageScore +
    summary.counts.belowWithoutInjection * 35 +
    summary.counts.injectionInconsistent * 18 +
    summary.counts.failures * 12 +
    (summary.generators?.problem || 0) * 260 +
    (summary.generators?.warning || 0) * 24;
  return Math.round(score);
}

export function getSummaryDashboardStats(summaries, translate) {
  const totalCages = summaries.reduce((sum, summary) => sum + summary.cagesTotal, 0);
  const cages = summaries.flatMap((summary) => summary.cages);
  const belowThreshold = summaries.reduce((sum, summary) => sum + summary.counts.belowThreshold, 0);
  const injecting = summaries.reduce((sum, summary) => sum + summary.counts.injecting, 0);
  const belowWithoutInjection = summaries.reduce((sum, summary) => sum + summary.counts.belowWithoutInjection, 0);
  const cageFailures = summaries.reduce((sum, summary) => sum + summary.counts.failures, 0);
  const generatorFailures = summaries.reduce((sum, summary) => sum + (summary.generators?.problem || 0), 0);
  const generatorWarnings = summaries.reduce((sum, summary) => sum + (summary.generators?.warning || 0), 0);
  const sensorProblemCounts = cages.reduce(
    (acc, cage) => {
      toArray(cage.visibleSensorProblems).forEach((problem) => {
        if (problem?.type in acc) acc[problem.type] += 1;
      });
      return acc;
    },
    { o2: 0, temperature: 0, salinity: 0 }
  );

  return {
    activeCenters: summaries.length,
    criticalCenters: summaries.filter((summary) => summary.status === "critical").length,
    warningCenters: summaries.filter((summary) => summary.status === "warning").length,
    injectingCenters: summaries.filter((summary) => summary.status === "injecting").length,
    normalCenters: summaries.filter((summary) => summary.status === "normal").length,
    offlineCenters: summaries.filter((summary) => summary.status === "offline" || summary.status === "stale").length,
    totalCages,
    reliableCages: cages.filter((cage) => cage.reliability.reliable).length,
    unreliableCages: cages.filter((cage) => !cage.reliability.reliable).length,
    manualCages: cages.filter((cage) => !cage.injection.modeAuto).length,
    belowThreshold,
    belowThresholdPercent: totalCages ? Math.round((belowThreshold / totalCages) * 100) : 0,
    injecting,
    injectingPercent: totalCages ? Math.round((injecting / totalCages) * 100) : 0,
    belowInjecting: cages.filter((cage) => cage.belowThreshold && cage.injection.injecting).length,
    belowAutoNoInjection: cages.filter((cage) => isActionableBelowWithoutInjection(cage) && cage.injection.modeAuto).length,
    belowManual: cages.filter((cage) => cage.belowThreshold && !cage.injection.modeAuto).length,
    injectingAboveThreshold: cages.filter((cage) => cage.injection.injecting && !cage.belowThreshold).length,
    belowWithoutInjection,
    belowWithoutInjectionPercent: totalCages ? Math.round((belowWithoutInjection / totalCages) * 100) : 0,
    failures: cageFailures + generatorFailures,
    cageFailures,
    generatorFailures,
    generatorWarnings,
    commFailures: cages.filter((cage) => cage.commFailure).length,
    pressureInjectionFailures: cages.filter((cage) => cage.pressureInjectionFailure).length,
    alarmFailures: cages.filter((cage) => cage.alarmFailure).length,
    sensorFailures: summaries.reduce((sum, summary) => sum + summary.counts.sensorFailures, 0),
    sensorProblemCounts,
    centerDetailItems: summaries.map((summary) => `${summary.centerName}: ${getSummaryStatusLabel(summary.status, translate)}`),
    cageDetailItems: summaries.map((summary) => `${summary.centerName}: ${summary.cagesTotal} ${t(translate, "summary_v3_cages_lower", "jaulas")}`),
    belowThresholdItems: cages
      .filter((cage) => cage.belowThreshold)
      .map(
        (cage) =>
          `${getCageDisplayName(cage, translate)} - O2 ${fmtNum(cage.o2)} < ${t(
            translate,
            "summary_v3_min_threshold_lower",
            "umbral min"
          )} ${fmtNum(cage.threshold)} (${getCageShortTag(cage, translate)})`
      ),
    injectingItems: cages
      .filter((cage) => cage.injection.injecting)
      .map(
        (cage) =>
          `${getCageDisplayName(cage, translate)} - ${
            cage.belowThreshold
              ? t(translate, "summary_v3_below_min_threshold_lower", "bajo umbral min")
              : t(translate, "summary_v3_above_max_threshold_lower", "sobre umbral max")
          }`
      ),
    belowWithoutInjectionItems: cages
      .filter(isActionableBelowWithoutInjection)
      .map(
        (cage) =>
          `${getCageDisplayName(cage, translate)} - ${
            cage.injection.modeAuto
              ? t(translate, "summary_v3_auto_no_injection_lower", "auto sin inyeccion")
              : t(translate, "manual", "manual")
          }`
      ),
    failureItems: [
      ...summaries.flatMap((summary) =>
        summary.generators.items
          .filter((generator) => generator.tone === "danger")
          .map((generator) => `${summary.centerName} / ${t(translate, "generator", "Generador")} ${generator.name} - ${generator.statusLabel}`)
      ),
      ...cages
      .filter((cage) => cage.visibleFailure)
      .map((cage) => `${getCageDisplayName(cage, translate)} - ${getCageFailureDetail(cage, translate)}`),
    ],
    sensorFailureItems: cages.flatMap((cage) =>
      toArray(cage.visibleSensorProblems).map((problem) => `${getCageDisplayName(cage, translate)} - ${problem.label}: ${problem.detail}`)
    ),
  };
}

function getCageDisplayName(cage, translate) {
  return `${cage.centerName || t(translate, "centro", "Centro")} / ${t(translate, "jaula", "Jaula")} ${cage.alias ?? "-"}`;
}

function getCageFailureDetail(cage, translate) {
  const details = [];
  if (cage.commFailure) details.push(t(translate, "summary_v3_no_communication_lower", "sin comunicacion"));
  if (cage.pressureInjectionFailure) details.push(t(translate, "summary_v3_pressure_injection", "presion / inyeccion"));
  if (cage.alarmFailure) details.push(cage.alarmCodes?.map((code) => getAlarmLabel(code, translate)).join(", ") || t(translate, "alerta", "alarma"));
  if (cage.visibleSensorProblems?.length) details.push(cage.visibleSensorProblemLabel);
  return details.filter(Boolean).join(" + ") || t(translate, "summary_v3_problem_lower", "problema");
}

export function formatKpiDetailItems(items, limit = 8, translate) {
  const safeItems = toArray(items).filter(Boolean);
  if (!safeItems.length) return [t(translate, "summary_v3_no_records", "Sin registros")];
  const visibleItems = safeItems.slice(0, limit);
  if (safeItems.length > limit) visibleItems.push(`+${safeItems.length - limit} ${t(translate, "summary_v3_more_lower", "mas")}`);
  return visibleItems;
}

function getCageTrendDelta(cage) {
  const values = toArray(cage?.bomba?.latest_measurements?.O2)
    .map(Number)
    .filter(isNum);
  if (values.length < 2) return null;
  return values[values.length - 1] - values[0];
}

export function getCageShortTag(cage, translate) {
  const trend = getCageTrendDelta(cage);
  if (cage.visibleSensorProblems?.length) return cage.visibleSensorProblemLabel;
  if (cage.alarmFailure) return getAlarmLabel(cage.alarmCodes?.[0], translate);
  if (cage.belowThreshold && !cage.injection.injecting) {
    if (cage.injection.turnedOff) return t(translate, "summary_v3_turned_off_f", "Apagada");
    if (!cage.injection.modeAuto) return t(translate, "manual", "Manual");
    if (cage.lowPressure) return t(translate, "summary_v3_auto_no_pressure", "Auto sin presion");
    return t(translate, "summary_v3_auto_no_injection", "Auto sin inyeccion");
  }
  if (cage.injection.injecting) return t(translate, "summary_v3_legend_injecting", "Inyectando");
  if (cage.o2RangeTone === "between") return t(translate, "summary_v3_between_thresholds", "Entre umbrales");
  if (cage.o2RangeTone === "ok") return t(translate, "summary_v3_above_max_threshold_lower", "Sobre umbral max");
  if (isNum(trend) && trend < 0) return t(translate, "summary_v3_decreasing", "Bajando");
  return t(translate, "summary_v3_legend_normal", "Normal");
}

function getCageDetailRows(cage, translate) {
  return [
    [t(translate, "summary_v3_current_o2", "O2 actual"), fmtNum(cage.o2)],
    [t(translate, "temperatura", "Temperatura"), fmtNum(cage.temperature)],
    [t(translate, "salinidad", "Salinidad"), fmtNum(cage.salinity)],
    [t(translate, "summary_v3_min_threshold", "Umbral Min"), fmtNum(cage.threshold)],
    [t(translate, "summary_v3_max_threshold", "Umbral Max"), fmtNum(cage.highThreshold)],
    [t(translate, "mode", "Modo"), cage.injection.modeAuto ? t(translate, "summary_v3_automatic", "Automatico") : t(translate, "manual", "Manual")],
    [
      t(translate, "summary_v3_injection", "Inyeccion"),
      cage.injection.injecting ? t(translate, "go3d_status_active_f", "Activa") : t(translate, "summary_v3_stopped_f", "Detenida"),
    ],
    [t(translate, "summary_v3_detail", "Detalle"), getCageShortTag(cage, translate)],
    [t(translate, "summary_v3_last_data", "Ultimo dato"), formatCompactDateTime(cage.lastUpdate)],
  ];
}

function getCenterGeneratorsSummary(center, translate) {
  const generators = normalizeGenerators(center?.generadores ?? center?.generators ?? center?.generator);
  const items = generators.map((generator, index) => ({
    ...getGeneratorSimpleStatus(generator, translate),
    generator,
    name: getGeneratorLabel(generator, index),
  }));

  return {
    total: items.length,
    items,
    active: items.filter((item) => item.tone === "ok").length,
    warning: items.filter((item) => item.tone === "warning").length,
    problem: items.filter((item) => item.tone === "danger").length,
    off: items.filter((item) => item.tone === "muted").length,
  };
}

function normalizeGenerators(value) {
  if (Array.isArray(value)) {
    if (value.every((item) => Array.isArray(item) && item.length === 2)) {
      return value.map(([, generator]) => generator).filter(Boolean);
    }
    return value.filter(Boolean);
  }
  if (!value || typeof value !== "object") return [];
  const keys = Object.keys(value);
  if (keys.length > 1 && !("hf_status" in value) && !("plc_status" in value)) {
    return Object.values(value).filter(Boolean);
  }
  return [value];
}

function getGeneratorLabel(generator, index) {
  return generator?.name || generator?.nombre || generator?.id || generator?.id_generador || `Gen ${index + 1}`;
}

function getGeneratorSimpleStatus(generator, translate) {
  if (!generator || typeof generator !== "object") return { statusLabel: t(translate, "no_data", "Sin datos"), tone: "muted" };

  if (isTekFish(generator)) {
    const statuses = Array.isArray(generator.connection_status) ? generator.connection_status : [];
    const commOk = statuses.includes(true);
    if (!commOk) return { statusLabel: t(translate, "sin_com", "Sin comunicacion"), tone: "warning" };
    if (generator.falla_generador_tekfish) return { statusLabel: t(translate, "alerta_generador", "Alarma en Generador"), tone: "danger" };
    if (generator.digital_outputs_state === "10") return { statusLabel: t(translate, "Señal_on_enviada", "Senal de Encendido Enviada"), tone: "ok" };
    if (generator.digital_outputs_state === "01") return { statusLabel: t(translate, "Señal_off_enviada", "Senal de Apagado Enviada"), tone: "muted" };
    return { statusLabel: t(translate, "unknown_status", "Estado desconocido"), tone: "warning" };
  }

  if ("plc_status" in generator) {
    if (generator.plc_status === 100) return { statusLabel: t(translate, "alerta_generador", "Alarma en Generador"), tone: "danger" };
    if (generator.plc_status === -1) return { statusLabel: t(translate, "sin_comunicacion_generador", "Sin comunicacion con Generador"), tone: "warning" };
    if (generator.partida === 100 && generator.detencion === 60) return { statusLabel: t(translate, "Señal_on_enviada", "Senal de Encendido Enviada"), tone: "ok" };
    if (generator.partida === 120 && generator.detencion === 60) return { statusLabel: t(translate, "generator_on_manual", "Generador Encendido Manualmente"), tone: "ok" };
    if (generator.partida === 60 && generator.detencion === 100) return { statusLabel: t(translate, "Señal_off_enviada", "Senal de Apagado Enviada"), tone: "muted" };
    return { statusLabel: t(translate, "sin_informacion_generador", "Sin Informacion Generador"), tone: "muted" };
  }

  if ("hf_status" in generator) {
    if (generator.hf_status === -2) return { statusLabel: t(translate, "sin_comunicacion_generador", "Sin comunicacion con Generador"), tone: "warning" };
    if (generator.hf_status === 100) return { statusLabel: t(translate, "Señal_on_enviada", "Senal de Encendido Enviada"), tone: "ok" };
    if (generator.hf_status === 120) return { statusLabel: t(translate, "generator_on_manual", "Generador Encendido Manualmente"), tone: "ok" };
    if (generator.hf_status === 80) return { statusLabel: t(translate, "off_generator", "Apagando Generador..."), tone: "warning" };
    if (generator.hf_status === 60) return { statusLabel: t(translate, "Señal_off_enviada", "Senal de Apagado Enviada"), tone: "muted" };
    return { statusLabel: `${t(translate, "status", "Estado")} HF ${generator.hf_status}`, tone: "warning" };
  }

  const state = String(generator.STATE || "").toUpperCase();
  const commOk = Array.isArray(generator.connection_status)
    ? generator.connection_status.includes(true)
    : generator.communication_status !== false;
  if (!commOk) return { statusLabel: t(translate, "sin_com", "Sin comunicacion"), tone: "warning" };

  const outsOn = generatorIsOn(generator);
  const order = resolveRunOrder(generator);

  // tone "danger" alimenta "Generadores criticos" y suma 260 al score de
  // severidad del centro, asi que un "011" valido no puede caer aqui.
  const outsBad =
    outputsAreInconsistent(generator.digital_outputs_state, generator.control_generator_back) ||
    (order !== null && outsOn !== null && order !== outsOn);

  if (outsBad) return { statusLabel: t(translate, "mismatch", "Inconsistencia de salidas"), tone: "danger" };
  if (state === "HOLD_BEFORE_OFF") return { statusLabel: t(translate, "shuttingDown", "Apagando..."), tone: "warning" };
  if (state === "WARMUP") return { statusLabel: t(translate, "warming", "Calentando"), tone: "warning" };
  if (state === "IS_ON" && outsOn === true) return { statusLabel: t(translate, "Señal_on_enviada", "Senal de Encendido Enviada"), tone: "ok" };
  if (order === false && outsOn === false) return { statusLabel: t(translate, "Señal_off_enviada", "Senal de Apagado Enviada"), tone: "muted" };
  if (order === true) return { statusLabel: t(translate, "Señal_on_enviada", "Senal de Encendido Enviada"), tone: "ok" };
  return { statusLabel: t(translate, "idle", "En espera"), tone: "muted" };
}

function getGeneratorsSummaryLabel(summary, translate) {
  if (!summary?.total) return t(translate, "no_data", "Sin datos");
  const byPriority = ["danger", "warning", "ok", "muted"];
  for (const tone of byPriority) {
    const item = summary.items.find((generator) => generator.tone === tone);
    if (item) return item.statusLabel;
  }
  return summary.items[0]?.statusLabel || t(translate, "no_data", "Sin datos");
}

function getGeneratorsTooltipRows(summary, translate) {
  if (!summary?.total) return [[t(translate, "status", "Estado"), t(translate, "summary_v3_no_generators", "Sin generadores")]];
  return summary.items.map((item) => [item.name, item.statusLabel]);
}

export function getSimpleCageTone(cage) {
  if (cage.commFailure) return "problem";
  if (cage.visibleSensorProblems?.length) return "problem";
  if (cage.operationalFailure) return "problem";
  if (cage.injection.injecting) return "injecting";
  if (cage.injection.turnedOff) return "normal";
  if (!cage.injection.modeAuto) return "manual";
  if (cage.belowThreshold) return "warning";
  return "normal";
}

export function getSimpleDistribution(summaries) {
  const distribution = { normal: 0, injecting: 0, manual: 0, warning: 0, problem: 0, generatorProblems: 0, total: 0 };
  summaries.forEach((summary) => {
    summary.cages.forEach((cage) => {
      distribution[getSimpleCageTone(cage)] += 1;
    });
    distribution.generatorProblems += summary.generators?.problem || 0;
  });
  distribution.problem += distribution.generatorProblems;
  distribution.total = distribution.normal + distribution.injecting + distribution.manual + distribution.warning + distribution.problem;
  return distribution;
}

export function isCenterHealthy(summary) {
  return (
    summary.status === "normal" &&
    !summary.counts.belowThreshold &&
    !summary.counts.belowWithoutInjection &&
    !summary.counts.injecting &&
    !summary.counts.manualCages &&
    !summary.counts.injectionInconsistent &&
    !summary.counts.failures &&
    !summary.counts.totalFailures &&
    !summary.counts.unreliable &&
    !summary.generators?.warning &&
    !summary.generators?.problem
  );
}

export function getMapCenterRank(summary) {
  if (
    summary.status === "critical" ||
    summary.status === "offline" ||
    summary.status === "stale" ||
    summary.counts.failures ||
    summary.counts.totalFailures ||
    summary.counts.unreliable
  ) return 0;
  if (summary.status === "warning" || summary.counts.belowThreshold || summary.counts.belowWithoutInjection || summary.counts.manualCages) return 1;
  if (summary.cages.some((cage) => cage.injection.injecting)) return 2;
  return 4;
}

export function getMapCenterTone(summary) {
  if (
    summary.status === "critical" ||
    summary.status === "offline" ||
    summary.status === "stale" ||
    summary.counts.failures ||
    summary.counts.totalFailures ||
    summary.counts.unreliable
  ) return "problem";
  if (summary.status === "warning" || summary.counts.belowThreshold || summary.counts.belowWithoutInjection) return "warning";
  if (summary.cages.some((cage) => cage.injection.injecting)) return "injecting";
  if (summary.counts.manualCages) return "manual";
  return "normal";
}

function getLatestTimestampFromCage(bomba) {
  const ts = toArray(bomba?.latest_measurements?.TS);
  return lastOf(ts) || null;
}

function parseDashboardDateTime(value) {
  if (!value || typeof value !== "string") return null;
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isMeasurementStale(value, minutes = 40) {
  const measuredAt = parseDashboardDateTime(value);
  if (!measuredAt) return true;
  const diffMs = Date.now() - measuredAt.getTime();
  return diffMs >= minutes * 60 * 1000;
}

function formatCompactDateTime(value) {
  const parsed = parseDashboardDateTime(value);
  if (!parsed) return "-";
  try {
    return new Intl.DateTimeFormat("es-CL", {
      timeZone: "America/Santiago",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(parsed);
  } catch {
    return value || "-";
  }
}

function firstAvailable(...vals) {
  for (const value of vals) {
    if (value === null || value === undefined || value === "") continue;
    if (isNum(Number(value))) return Number(value);
  }
  return null;
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function lastOf(arr) {
  return arr && arr.length ? arr[arr.length - 1] : null;
}

function isNum(value) {
  return typeof value === "number" && Number.isFinite(value);
}

export function fmtNum(value) {
  if (!isNum(Number(value))) return "-";
  return new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(Number(value));
}
